import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useListExternalLayersQuery } from 'hooks/externalLayer/useListExternalLayersQuery';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';

import {
  isQueryableFeatureService,
  listArcgisFeatures,
  type ArcgisFeatureChoice,
  type ArcgisLocationLink,
} from './arcgisFeatureLocation';

const NONE = '__none__';

export function ArcgisFeatureLocationField({
  link,
  onSelectFeature,
  onClear,
}: {
  link: ArcgisLocationLink | null;
  onSelectFeature: (
    next: ArcgisLocationLink,
    point: { longitude: number; latitude: number }
  ) => void;
  onClear: () => void;
}) {
  const groupGuid = useAppSelector(selectGroupGuid);
  const layersQuery = useListExternalLayersQuery(
    {
      filter: { groupGuid: groupGuid ?? '' },
      perPage: 200,
    },
    { enabled: Boolean(groupGuid) }
  );
  const layers = (layersQuery.data?.data ?? []).filter(
    (layer) => !layer.incidentId && isQueryableFeatureService(layer.serviceUrl)
  );
  const [layerId, setLayerId] = useState(link?.external_layer_id ?? '');
  const [features, setFeatures] = useState<ArcgisFeatureChoice[]>([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [featureError, setFeatureError] = useState(false);

  useEffect(() => {
    if (link?.external_layer_id) setLayerId(link.external_layer_id);
  }, [link?.external_layer_id]);

  const serviceUrl =
    layers.find((item) => item.id === layerId)?.serviceUrl ?? '';

  useEffect(() => {
    if (!serviceUrl) {
      setFeatures([]);
      return;
    }
    let cancelled = false;
    setLoadingFeatures(true);
    setFeatureError(false);
    listArcgisFeatures(serviceUrl)
      .then((next) => {
        if (!cancelled) setFeatures(next);
      })
      .catch(() => {
        if (!cancelled) {
          setFeatures([]);
          setFeatureError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingFeatures(false);
      });
    return () => {
      cancelled = true;
    };
  }, [serviceUrl]);

  const selectedLayer = layers.find((layer) => layer.id === layerId);
  const selectedFeature = features.find(
    (feature) => feature.objectId === link?.object_id
  );

  return (
    <div className="tw:space-y-2">
      <Field>
        <FieldLabel>Location from ArcGIS layer</FieldLabel>
        <Select
          value={layerId || NONE}
          onValueChange={(next) => {
            const resolved = String(next);
            const nextId = resolved === NONE ? '' : resolved;
            setLayerId(nextId);
            if (link && nextId !== link.external_layer_id) onClear();
          }}
        >
          <SelectTrigger size="sm" className="tw:w-full">
            <SelectValue placeholder="Choose a layer">
              {selectedLayer?.name ?? 'Choose a layer'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="dark">
            <SelectItem value={NONE}>Choose a layer</SelectItem>
            {layers.map((layer) => (
              <SelectItem key={layer.id} value={layer.id}>
                {layer.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      {layerId ? (
        <Field>
          <FieldLabel>Layer object</FieldLabel>
          <Select
            value={
              link?.external_layer_id === layerId && link.object_id != null
                ? String(link.object_id)
                : NONE
            }
            disabled={loadingFeatures || features.length === 0}
            onValueChange={(next) => {
              const resolved = String(next);
              if (resolved === NONE) return;
              const feature = features.find(
                (item) => String(item.objectId) === resolved
              );
              if (!feature) return;
              onSelectFeature(
                {
                  external_layer_id: layerId,
                  object_id: feature.objectId,
                },
                {
                  longitude: feature.longitude,
                  latitude: feature.latitude,
                }
              );
            }}
          >
            <SelectTrigger size="sm" className="tw:w-full">
              <SelectValue
                placeholder={
                  loadingFeatures ? 'Loading objects…' : 'Choose an object'
                }
              >
                {selectedFeature?.label ??
                  (loadingFeatures ? 'Loading objects…' : 'Choose an object')}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="dark">
              <SelectItem value={NONE}>Choose an object</SelectItem>
              {features.map((feature) => (
                <SelectItem
                  key={feature.objectId}
                  value={String(feature.objectId)}
                >
                  {feature.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {featureError ? (
            <p className="tw:mb-0 tw:text-xs tw:text-destructive">
              Could not read objects from this layer.
            </p>
          ) : null}
        </Field>
      ) : null}
      {link ? (
        <Button type="button" variant="outline" size="sm" onClick={onClear}>
          Clear ArcGIS link
        </Button>
      ) : null}
    </div>
  );
}
